import { useEffect, useRef, useState } from "react";

import { differenceInCalendarDays, format, parse } from "date-fns";
import isNil from "lodash/isNil";
import { isIOS, isMobile } from "react-device-detect";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  DownloadEnforcementAnalyticsEvent,
  DownloadEnforcementAnalyticsPageView,
  DownloadEnforcementPlatform,
} from "@/constants/analytics/Analytics";
import { PublicAppURL } from "@/constants/Application";
import { DateFormat } from "@/constants/DateFormat";
import type { DownloadEnforcementStorageKey } from "@/hooks/useUnencryptedStorage";
import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import { useAnalytics } from "@/services/Analytics";
import { captureException } from "@/services/AppMonitor";
import { openUrl } from "@/utils/navigation.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

// the modal re-appears once per device, per user, every 7 days
const DismissalIntervalDays = 7;

// MMKV is device-local, so keying by the app user's id gives us the required
// "per device, per user" scoping
const buildStorageKey = (appUserId: string): DownloadEnforcementStorageKey =>
  `${UnencryptedStorageKey.DownloadEnforcementDismissedDate}_${appUserId}`;

const hasDismissalIntervalElapsed = (storedDate: string): boolean => {
  const parsed = parse(storedDate, DateFormat.yearMonthDay, new Date());
  // show again if the stored date is unparsable or the interval has elapsed
  if (Number.isNaN(parsed.getTime())) return true;
  return differenceInCalendarDays(new Date(), parsed) >= DismissalIntervalDays;
};

const getShouldShowModal = ({
  appUserId,
  getStoredDismissalDate,
}: {
  appUserId: string;
  getStoredDismissalDate: (
    key: DownloadEnforcementStorageKey,
  ) => string | undefined;
}): boolean => {
  // shown on all web (desktop = QR variant, web-on-mobile = download CTA variant);
  // gated only by having an app user + the 7-day dismissal window
  if (!isNotNilNotEmpty(appUserId)) return false;

  const storedDate = getStoredDismissalDate(buildStorageKey(appUserId));
  // never dismissed on this device → show
  if (isNil(storedDate)) return true;

  return hasDismissalIntervalElapsed(storedDate);
};

const useDownloadEnforcementModal = (appUserId: string) => {
  const { track, trackPageView } = useAnalytics();
  const { getUnencryptedStringValue, saveUnencryptedStringValue } =
    useUnencryptedStorage();

  const [isOpen, setIsOpen] = useState(false);
  // once the user dismisses (close or download) we must not re-open this session,
  // even as eligibility re-evaluates
  const hasDismissedThisSessionRef = useRef(false);

  // re-evaluate eligibility when appUserId changes (it can start empty in Redux
  // state and populate after login), but never re-open after a dismissal
  useEffect(() => {
    if (hasDismissedThisSessionRef.current) return;
    setIsOpen(
      getShouldShowModal({
        appUserId,
        getStoredDismissalDate: getUnencryptedStringValue,
      }),
    );
  }, [appUserId, getUnencryptedStringValue]);

  // desktop web shows the QR variant; web-on-mobile shows the download CTA variant.
  // The Platform property ships on every event so both report into the same funnel
  // and can be segmented desktop vs web-on-mobile.
  const platform = isMobile
    ? DownloadEnforcementPlatform.WebOnMobile
    : DownloadEnforcementPlatform.Desktop;

  const dismiss = () => {
    hasDismissedThisSessionRef.current = true;
    setIsOpen(false);
    if (!isNotNilNotEmpty(appUserId)) return;
    saveUnencryptedStringValue({
      key: buildStorageKey(appUserId),
      value: format(new Date(), DateFormat.yearMonthDay),
    });
  };

  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (!isOpen || hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    trackPageView(DownloadEnforcementAnalyticsPageView.Modal, {
      [AnalyticsFieldName.Platform]: platform,
    });
    track(DownloadEnforcementAnalyticsEvent.Viewed, {
      [AnalyticsFieldName.Platform]: platform,
    });
  }, [isOpen, platform, track, trackPageView]);

  const handleClose = () => {
    track(DownloadEnforcementAnalyticsEvent.ClosePressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Platform]: platform,
    });
    dismiss();
  };

  const handleDownload = () => {
    const storeUrl = isIOS ? PublicAppURL.iOS : PublicAppURL.android;
    track(DownloadEnforcementAnalyticsEvent.DownloadPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Platform]: platform,
      [AnalyticsFieldName.URL]: storeUrl,
    });
    void openUrl({
      onError: (error) => captureException(error),
      url: storeUrl,
    });
    dismiss();
  };

  return {
    handleDownloadEnforcementClose: handleClose,
    handleDownloadEnforcementDownload: handleDownload,
    // desktop web renders the QR variant; web-on-mobile renders the download CTA
    isDownloadEnforcementDesktop: !isMobile,
    isDownloadEnforcementModalOpen: isOpen,
  };
};

export default useDownloadEnforcementModal;
